import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DecomActionModel, DecomModel } from '../../../../types';
import SimpleDrawer from '../../../shared/simple-drawer';
import { getAllDecomModels, UserKeys } from '../../../api/users-api';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import { Messages } from '../../../utils/message-constants';
import { useDecomPolicyActions } from './hooks/use-decom-policy-actions';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import AvatarChip from '../../../shared/avatar-chip';
import SharedActionAccordion from '../../../shared/shared-action-accordion';
import { decomActions } from '../../../shared/decom-actions';
import { decomNarratives } from './decom-policy-form/form-steps/decom-review-narratives';
import StatusLabel from '../../../shared/status-label';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import MoreVert from '@mui/icons-material/MoreVert';
import Update from '@mui/icons-material/Update';

interface DecomPolicyDrawerContentProps {
  selectedPolicy: DecomModel;
  onClose: () => void;
}

function DecomPolicyDrawerContent({
  selectedPolicy,
  onClose,
}: DecomPolicyDrawerContentProps) {
  const { toolsRoles, currentTier, apiUrl } = useUserSettings();
  const { handleEditPolicy, handleClonePolicy } = useDecomPolicyActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const canEdit = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.EditDecommissioningProcesses.value
  );
  const canClone = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CreateDecommissioningProcesses.value
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const formatDate = (timestamp: number) =>
    timestamp ? new Date(timestamp).toLocaleString() : 'N/A';

  const renderBooleanIcon = (value: boolean) =>
    value ? (
      <Check
        fontSize="small"
        sx={{ verticalAlign: 'middle', color: 'text.secondary' }}
      />
    ) : (
      <Close
        fontSize="small"
        sx={{ verticalAlign: 'middle', color: 'text.secondary' }}
      />
    );

  const basicInfoData: Record<string, React.ReactNode> = useMemo(
    () => ({
      'Policy Name': selectedPolicy.name,
      'Trigger Type':
        selectedPolicy.triggerType === 'suspension'
          ? 'User Suspension'
          : 'Org Unit Change',
      Status: (
        <StatusLabel status={selectedPolicy.isActive ? 'ACTIVE' : 'DISABLED'} />
      ),
      'Act Immediately': renderBooleanIcon(selectedPolicy.actImmediately),
      'Org Units':
        selectedPolicy.org?.length > 0 ? (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {selectedPolicy.org.map((ou) => (
              <AvatarChip key={ou} email={ou} type="OrgUnit" size="small" />
            ))}
          </Stack>
        ) : (
          'N/A'
        ),
      'Send Summary Email': renderBooleanIcon(selectedPolicy.sendSummary),
      ...(selectedPolicy.notificationUser && {
        'Summary Email': (
          <AvatarChip
            email={selectedPolicy.notificationUser}
            type="Unknown"
            size="small"
          />
        ),
      }),
      'Leave Unsuspended': renderBooleanIcon(selectedPolicy.keepUnsuspended),
      'Pause Decom On Org Change': renderBooleanIcon(
        selectedPolicy.validateUserIsStillInDecomOrg
      ),
      'Pause Decom On Unsuspension': renderBooleanIcon(
        selectedPolicy.validateUserIsStillSuspended
      ),
      ...(selectedPolicy.createdBy && {
        'Created By': (
          <AvatarChip
            email={selectedPolicy.createdBy}
            type="User"
            size="small"
          />
        ),
      }),
      ...(selectedPolicy.lastUpdatedBy && {
        'Last Updated By': (
          <AvatarChip
            email={selectedPolicy.lastUpdatedBy}
            type="User"
            size="small"
          />
        ),
      }),
      ...(currentTier === 'enterprise' &&
        selectedPolicy.id && {
          'Decommissioning ID': selectedPolicy.id,
        }),
      ...(currentTier === 'enterprise' &&
        apiUrl && {
          'API Endpoint': `${apiUrl}/v1/policy/decommissioning`,
        }),
    }),
    [selectedPolicy, currentTier, apiUrl]
  );

  const actionConfigData: Record<string, React.ReactNode> | null =
    useMemo(() => {
      const hasDefaultEmails =
        selectedPolicy.defaultUserToNotify &&
        selectedPolicy.defaultUserToNotify.length > 0;
      const hasDestUser =
        selectedPolicy.defaultUseManagerEmail !== null ||
        selectedPolicy.defaultDestUser !== null;

      if (!hasDefaultEmails && !hasDestUser) return null;

      const destUserValue =
        selectedPolicy.defaultUseManagerEmail === true ? (
          <AvatarChip email="Manager" type="Unknown" size="small" />
        ) : selectedPolicy.defaultDestUser ? (
          <AvatarChip
            email={selectedPolicy.defaultDestUser.userEmail}
            type={
              selectedPolicy.defaultDestUser.type as
                | 'User'
                | 'Group'
                | 'Unknown'
            }
            size="small"
          />
        ) : (
          'No Default Destination User'
        );

      return {
        ...(hasDefaultEmails && {
          'Default Emails to Notify': (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {selectedPolicy.defaultUserToNotify!.map((email) => (
                <AvatarChip
                  key={email}
                  email={email}
                  type="Unknown"
                  size="small"
                />
              ))}
            </Stack>
          ),
        }),
        'Default Destination User': destUserValue,
      };
    }, [selectedPolicy]);

  const groupedActions = useMemo(() => {
    const actionsData = selectedPolicy.actions || {};
    const grouped = Object.values(actionsData).reduce(
      (acc, { actions, day }) => {
        if (!acc[day]) acc[day] = [];
        acc[day].push(...(actions || []));
        return acc;
      },
      {} as Record<number, DecomActionModel[]>
    );
    const sortedDays = Object.keys(grouped)
      .map(Number)
      .sort((a, b) => a - b);
    return { grouped, sortedDays };
  }, [selectedPolicy]);

  return (
    <SimpleDrawer title="Decom Policy Details" onClose={onClose}>
      <Stack pl={2} pr={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {selectedPolicy.name || 'N/A'}
          </Typography>
          <IconButton
            onClick={handleMenuClick}
            id="decom-policy-details-menu-button"
            size="small"
          >
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            elevation={1}
            onClose={handleMenuClose}
            slotProps={{
              list: {
                'aria-labelledby': 'decom-policy-details-menu-button',
              },
            }}
          >
            <DisabledTooltipMenuItem
              onClick={() => {
                handleMenuClose();
                handleEditPolicy(selectedPolicy);
              }}
              disabled={!canEdit}
              data-disabled-reason={
                !canEdit
                  ? Messages.Errors.Permission.Acting(
                      ToolsRoles.EditDecommissioningProcesses.name
                    )
                  : undefined
              }
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              Edit
            </DisabledTooltipMenuItem>
            <DisabledTooltipMenuItem
              onClick={() => {
                handleMenuClose();
                handleClonePolicy(selectedPolicy);
              }}
              disabled={!canClone}
              data-disabled-reason={
                !canClone
                  ? Messages.Errors.Permission.Acting(
                      ToolsRoles.CreateDecommissioningProcesses.name
                    )
                  : undefined
              }
            >
              <ListItemIcon>
                <LibraryAdd fontSize="small" />
              </ListItemIcon>
              Clone
            </DisabledTooltipMenuItem>
          </Menu>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          sx={{ mb: 2, alignItems: 'center' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Update fontSize="small" />
            <Typography variant="body2" color="inherit">
              Last Updated: {formatDate(selectedPolicy.lastUpdatedTime)}
            </Typography>
          </Stack>
          <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
            •
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Check fontSize="small" />
            <Typography variant="body2" color="inherit">
              Created: {formatDate(selectedPolicy.createdTime)}
            </Typography>
          </Stack>
        </Stack>

        <Box sx={{ pb: 4 }}>
          <ModelDetailsDisplay title="Basic Information" data={basicInfoData} />

          {actionConfigData && (
            <ModelDetailsDisplay
              title="Action Configuration"
              data={actionConfigData}
            />
          )}

          {groupedActions.sortedDays.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 'bold', mb: 2, fontSize: 14 }}
              >
                Actions
              </Typography>
              {groupedActions.sortedDays.map((day, dayIndex) => (
                <Box key={day} sx={{ display: 'flex' }}>
                  {/* Timeline column */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mr: 2,
                      pt: 0.4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        flexShrink: 0,
                      }}
                    />
                    {dayIndex < groupedActions.sortedDays.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          flexGrow: 1,
                          minHeight: 24,
                          bgcolor: 'divider',
                          my: 0.75,
                        }}
                      />
                    )}
                  </Box>
                  {/* Day content */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      pb:
                        dayIndex < groupedActions.sortedDays.length - 1 ? 3 : 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}
                    >
                      {day === 0
                        ? 'First Run'
                        : `${day} Day${day === 1 ? '' : 's'} Out`}
                    </Typography>
                    <Stack spacing={1}>
                      {groupedActions.grouped[day].map(
                        (action: DecomActionModel, idx: number) => {
                          const decomAction = decomActions.find(
                            (a) => a.value === action.actionType
                          );
                          if (!decomAction) return null;
                          const summaryFn = decomNarratives[decomAction.value];
                          const summary =
                            typeof summaryFn === 'function'
                              ? summaryFn({
                                  ...action,
                                  gPanelTier: currentTier,
                                })
                              : summaryFn;
                          return (
                            <SharedActionAccordion
                              key={decomAction.value + idx}
                              id={decomAction.value + idx}
                              title={decomAction.label}
                              description={summary}
                              type={decomAction.groupType}
                              expanded={true}
                              onToggleExpand={() => {}}
                            />
                          );
                        }
                      )}
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Stack>
    </SimpleDrawer>
  );
}

export default function DecomPolicyDrawerDetailsOld() {
  const [searchParams] = useSearchParams();
  const selectedPolicyId = searchParams.get('selectedDecomPolicy');
  const { closeDrawer } = useDecomPolicyActions();

  const { data: decomModels, isLoading } = useQuery({
    queryKey: [UserKeys.DecomModels],
    queryFn: () => getAllDecomModels(),
    enabled: !!selectedPolicyId,
  });

  const selectedPolicy = Array.isArray(decomModels)
    ? decomModels.find((m) => m.id === selectedPolicyId) ?? null
    : null;

  return (
    <Drawer
      anchor="right"
      open={!!selectedPolicyId}
      onClose={closeDrawer}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100vw', md: '45%' } } }}
    >
      {isLoading ? (
        <SimpleDrawer
          title="Decom Policy Details"
          onClose={closeDrawer}
          isLoading
          children={undefined}
        />
      ) : selectedPolicy ? (
        <DecomPolicyDrawerContent
          key={selectedPolicy.id}
          selectedPolicy={selectedPolicy}
          onClose={closeDrawer}
        />
      ) : (
        <SimpleDrawer title="Decom Policy Details" onClose={closeDrawer}>
          <Typography sx={{ p: 3 }} color="error">
            Policy not found.
          </Typography>
        </SimpleDrawer>
      )}
    </Drawer>
  );
}
